'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense } from 'react';
import styles from '../styles/Home.module.css';

type Schedule = { days: number[]; hour: number; minute: number };
type Session = {
  image: string;
  title: string;
  subtitle: string;
  room: string;
  schedules: Schedule[];
  durationMin: number; // durée typique pour calculer la fin
};

// Days: 0=Dim, 1=Lun, 2=Mar, 3=Mer, 4=Jeu, 5=Ven, 6=Sam (heure de Paris)
const SESSIONS: Session[] = [
  {
    image: '/sessions/odyssee.jpg',
    title: 'Odyssée Anatman',
    subtitle: '',
    room: 'odyssee',
    schedules: [{ days: [0], hour: 16, minute: 30 }], // Dim 16:30
    durationMin: 90,
  },
  {
    image: '/sessions/3e-oeil.jpg',
    title: 'Le 3è Œil',
    subtitle: '',
    room: '3e-oeil',
    schedules: [{ days: [6], hour: 20, minute: 30 }], // Sam 20:30
    durationMin: 90,
  },
  {
    image: '/sessions/dialogue-avec-soi.jpg',
    title: 'Dialogue avec Soi',
    subtitle: '',
    room: 'dialogue-avec-soi',
    schedules: [{ days: [0], hour: 20, minute: 30 }], // Dim 20:30
    durationMin: 90,
  },
  {
    // Evenement ponctuel : aucune date annoncee pour l'instant, donc la vignette
    // reste masquee sur la page d'accueil. Le lien direct fonctionne quand meme.
    // Des qu'Alexandre donne la date, ajouter ici { days: [j], hour: h, minute: m }.
    image: '/sessions/webinaire.jpg',
    title: 'Webinaire Alexandre Anatman',
    subtitle: '',
    room: 'webinaire',
    schedules: [],
    durationMin: 90,
  },
  {
    image: '/sessions/yoga.jpg',
    title: 'Yoga',
    subtitle: '',
    room: 'yoga',
    schedules: [{ days: [2, 3, 4, 5, 6], hour: 8, minute: 0 }], // Mar-Sam 8:00
    durationMin: 60,
  },
];

const WINDOW_BEFORE_MIN = 60;   // visible 1h avant le début
const WINDOW_AFTER_END_MIN = 30; // visible jusqu'à 30 min après la fin

function getParisTimeMinutes(): number {
  // Renvoie la position dans la semaine en minutes (0 = Dim 00:00, ..., 10079 = Sam 23:59), heure Paris
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Paris',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(new Date()).map((p) => [p.type, p.value]),
  );
  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const day = dayMap[parts.weekday] ?? 0;
  const hour = parseInt(parts.hour, 10);
  const minute = parseInt(parts.minute, 10);
  return day * 1440 + hour * 60 + minute;
}

function isSessionVisibleNow(s: Session, nowMin: number): boolean {
  const afterMin = s.durationMin + WINDOW_AFTER_END_MIN;
  for (const sched of s.schedules) {
    for (const d of sched.days) {
      const start = d * 1440 + sched.hour * 60 + sched.minute;
      // Compare directement et avec décalage ±7j pour gérer le bord de semaine
      for (const off of [0, 7 * 1440, -7 * 1440]) {
        const delta = nowMin - (start + off);
        if (delta >= -WINDOW_BEFORE_MIN && delta <= afterMin) return true;
      }
    }
  }
  return false;
}

function formatNextStart(s: Session, nowMin: number): string {
  const dayLabels = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  let best: { day: number; hour: number; minute: number; deltaMin: number } | null = null;
  for (const sched of s.schedules) {
    for (const d of sched.days) {
      const start = d * 1440 + sched.hour * 60 + sched.minute;
      for (const off of [0, 7 * 1440]) {
        const delta = (start + off) - nowMin;
        if (delta < 0) continue;
        if (!best || delta < best.deltaMin) {
          best = { day: d, hour: sched.hour, minute: sched.minute, deltaMin: delta };
        }
      }
    }
  }
  if (!best) return '';
  const hh = String(best.hour).padStart(2, '0');
  const mm = String(best.minute).padStart(2, '0');
  return `prochain ${dayLabels[best.day]} ${hh}h${mm}`;
}

const MOD_STORAGE_KEY = 'anatman_mod_secret';

function HomeContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [modSecret, setModSecret] = React.useState<string>('');

  React.useEffect(() => {
    // Read mod secret from URL or sessionStorage
    const urlMod = searchParams?.get('mod') ?? '';
    if (urlMod) {
      sessionStorage.setItem(MOD_STORAGE_KEY, urlMod);
      setModSecret(urlMod);
    } else {
      const stored = sessionStorage.getItem(MOD_STORAGE_KEY) ?? '';
      setModSecret(stored);
    }
  }, [searchParams]);

  const exitModeratorMode = () => {
    sessionStorage.removeItem(MOD_STORAGE_KEY);
    setModSecret('');
    router.push('/');
  };

  const goToRoom = (room: string) => {
    if (modSecret) {
      router.push(`/rooms/${room}?mod=${encodeURIComponent(modSecret)}`);
    } else {
      router.push(`/rooms/${room}`);
    }
  };

  const isModerator = modSecret.length > 0;

  // Toutes les émissions sont visibles 24/7 — plus de filtre horaire
  const sessionsToShow = SESSIONS;
  const showNoSessionMsg = false;

  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className={styles.header}>
          {isModerator && (
            <div className={styles.modBadge}>
              🎬 Mode Modérateur actif
              <button
                type="button"
                onClick={exitModeratorMode}
                className={styles.modExit}
                title="Quitter le mode modérateur"
              >
                ✕
              </button>
            </div>
          )}
          <h1 className={styles.title}>{isModerator ? 'STUDIO ANATMAN' : 'ANATMAN TV'}</h1>
        </div>
        <div className={styles.cardGrid}>
          {sessionsToShow.map((s) => (
            <button
              key={s.room}
              className={styles.card}
              onClick={() => goToRoom(s.room)}
              aria-label={`Rejoindre ${s.title}`}
            >
              <div
                className={styles.cardImage}
                style={{ backgroundImage: `url(${s.image})` }}
                role="img"
                aria-label={s.title}
              />
              <div className={styles.cardBody}>
                <span className={styles.cardEnter}>
                  {isModerator ? 'DÉMARRER' : 'REJOINDRE'}
                </span>
              </div>
            </button>
          ))}
          <a
            href="https://www.anatman.tv/ff1c4db0-0ddd3664-85d67bc2"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.card}
            aria-label="Abonnement à Anatman TV"
            style={{ textDecoration: 'none' }}
          >
            <div
              className={styles.cardImage}
              style={{ backgroundImage: 'url(/sessions/abonnement.png)', backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundColor: '#2a1850' }}
              role="img"
              aria-label="Abonnement à Anatman TV"
            />
            <div className={styles.cardBody}>
              <span className={styles.cardEnter}>S&apos;ABONNER AUX REDIFFUSIONS</span>
            </div>
          </a>
          <a
            href="https://sebastiensocchard.systeme.io/f8930840-695e6508-d364f7a6-ece7011a"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.card}
            aria-label="Retraite Anatman"
            style={{ textDecoration: 'none' }}
          >
            <div
              className={styles.cardImage}
              style={{ backgroundImage: 'url(/sessions/retraite-dordogne.jpg)' }}
              role="img"
              aria-label="Retraite Anatman"
            />
            <div className={styles.cardBody}>
              <span className={styles.cardEnter}>DÉCOUVRIR</span>
            </div>
          </a>
        </div>
      </main>
      <footer data-lk-theme="default" className={styles.footer}>
        <span>Anatman&nbsp;Live &middot; Diffusion auto-hébergée</span>
      </footer>
    </>
  );
}

export default function Page() {
  return (
    <Suspense fallback={null}>
      <HomeContent />
    </Suspense>
  );
}
